<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: false,
    },
    target: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getPayload = (...args) => {
    const [data] = args;

    return props.target
      ? _.pick(data, targetConfig.value[props.target].fieldsToSubmit)
      : data;
  };

  const formId = ref(null);
  const defaultTitle = ref(
    $fn.value.pgettext('Contract', 'Edit General Information')
  );
  const targetConfig = ref({
    company: {
      title: $fn.value.pgettext('Contract', 'Edit company'),
      fieldsToSubmit: ['id', 'revision_id', 'etag', 'company'],
    },
    counterparty: {
      title: $fn.value.pgettext('Contract', 'Edit counterparty'),
      fieldsToSubmit: ['id', 'revision_id', 'etag', 'counterparty'],
    },
    invoicing: {
      title: $fn.value.pgettext('Contract', 'Edit invoicing information'),
      fieldsToSubmit: ['id', 'revision_id', 'etag', 'invoicing'],
    },
    lifecycle: {
      title: $fn.value.pgettext('Contract', 'Edit lifecycle information'),
      fieldsToSubmit: ['id', 'revision_id', 'etag', 'lifecycle'],
    },
    rental: {
      title: $fn.value.pgettext('Contract', 'Edit rental'),
      fieldsToSubmit: ['id', 'revision_id', 'etag', 'rental'],
    },
    general: {
      title: $fn.value.pgettext('Contract', 'Edit general data'),
      fieldsToSubmit: [
        'id',
        'revision_id',
        'etag',
        'extras',
        'revision_number',
        'status',
        'contract_type',
        'contract_number',
        'external_key',
        'category_id',
      ],
    },
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'md',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    defaultTitle: defaultTitle.value,
    targetConfig: targetConfig.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'defaultTitle' },
      { name: 'targetConfig' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId },
    ux: {},
    dataRefs: {
      formId,
      defaultTitle,
      targetConfig,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: props.target
          ? targetConfig.value[props.target].title
          : defaultTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.target,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('target');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_jdxsyyubw_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_5ubt44zub_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_q76x11sde_getActionArgs = async ($event) => {
    return {
      objectType: 'contract',
      op: 'upsert',
      data: getPayload(props.contract),
    };
  };

  const act_7315092382398562_closeModal_jdxsyyubw =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_jdxsyyubw_getActionArgs,
    });
  const inst_evh_7315092382398562_close_jc2b5xkxx =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_jdxsyyubw,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_jc2b5xkxx],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_e43al0awx =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_lijjw2d3t =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_e43al0awx,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_lijjw2d3t],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_ky8ehafel =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_1f4g4dgx3 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_ky8ehafel,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_1f4g4dgx3],
    event: 'close',
  });
  const act_1040167445267876_closeModal_5ubt44zub =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_5ubt44zub_getActionArgs,
    });
  const inst_evh_1040167445267876_success_t4ajpb4af =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_5ubt44zub,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_q76x11sde = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_q76x11sde_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_t4ajpb4af],
  });
  const inst_evh_1040167445267876_submit_mg24kb15n =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_q76x11sde,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_mg24kb15n],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_u0p27bso2 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_e3n7qoiwh =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_u0p27bso2,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_e3n7qoiwh],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_jc2b5xkxx,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_lijjw2d3t,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_1f4g4dgx3,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_t4ajpb4af,
    inst_evh_1040167445267876_submit_mg24kb15n,
    save: inst_evh_1040167445267876_submit_mg24kb15n,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_e3n7qoiwh,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_jc2b5xkxx = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_jc2b5xkxx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_lijjw2d3t = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_lijjw2d3t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_1f4g4dgx3 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_1f4g4dgx3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_t4ajpb4af = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_t4ajpb4af.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_mg24kb15n = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_mg24kb15n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_e3n7qoiwh = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_e3n7qoiwh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="contract"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <ContractEditorFieldset
            :contract="contract"
            :formId="formId"
            :target="target"
          >
          </ContractEditorFieldset>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms.contract || !forms.contract.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
